import React from "react";
import { Box, Typography, Button, Grid, Card, CardContent, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { MenuBook, Brush, Person } from "@mui/icons-material";

type Props = {};

const HomePage = (props: Props) => {
    const navigate = useNavigate();

    return (
        <Box sx={{ 
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
        }}>
            {/* Hero Section */}
            <Container maxWidth="lg">
                <Box 
                    sx={{ 
                        pt: { xs: 6, md: 10 }, 
                        pb: { xs: 6, md: 8 },
                        textAlign: 'center'
                    }}
                >
                    <Typography 
                        variant="h1" 
                        sx={{ 
                            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                            fontWeight: 700,
                            color: '#2c3e50',
                            mb: 2,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                    >
                        Philip Payne
                    </Typography>
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
                            fontWeight: 400,
                            color: '#7f8c8d',
                            mb: 4,
                            fontFamily: 'Georgia, serif',
                            fontStyle: 'italic'
                        }}
                    >
                        Author • Artist • Storyteller
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            color: '#555',
                            mb: 5,
                            maxWidth: '800px',
                            mx: 'auto',
                            lineHeight: 1.8,
                            fontFamily: 'Georgia, serif'
                        }}
                    >
                        From the heart of Birmingham to the pages of compelling narratives, 
                        explore a world where words paint pictures and art tells stories.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button 
                            variant="contained" 
                            size="large"
                            onClick={() => navigate('/bibliography')}
                            sx={{ 
                                backgroundColor: '#e74c3c',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                '&:hover': {
                                    backgroundColor: '#c0392b'
                                },
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            Explore Books
                        </Button>
                        <Button 
                            variant="outlined" 
                            size="large"
                            onClick={() => navigate('/bio')}
                            sx={{ 
                                borderColor: '#2c3e50',
                                color: '#2c3e50',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                '&:hover': {
                                    borderColor: '#2c3e50',
                                    backgroundColor: 'rgba(44, 62, 80, 0.05)'
                                },
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            About Philip
                        </Button>
                    </Box>
                </Box>

                {/* Feature Cards */}
                <Grid container spacing={4} sx={{ pb: 8 }}>
                    <Grid item xs={12} md={4}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                                }
                            }}
                            onClick={() => navigate('/bibliography')}
                        >
                            <Box 
                                sx={{ 
                                    p: 3, 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    backgroundColor: '#e74c3c'
                                }}
                            >
                                <MenuBook sx={{ fontSize: 60, color: 'white' }} />
                            </Box>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                                <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                                    Bibliography
                                </Typography>
                                <Typography sx={{ color: '#7f8c8d', lineHeight: 1.7 }}>
                                    Discover compelling narratives born from lived experience. 
                                    From "RON" to "A Nation's Cry", explore stories that resonate.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                                }
                            }}
                            onClick={() => navigate('/artist')}
                        >
                            <Box 
                                sx={{ 
                                    p: 3, 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    backgroundColor: '#3498db'
                                }}
                            >
                                <Brush sx={{ fontSize: 60, color: 'white' }} />
                            </Box>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                                <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                                    Art Gallery
                                </Typography>
                                <Typography sx={{ color: '#7f8c8d', lineHeight: 1.7 }}>
                                    Visual expressions that complement the written word. 
                                    Explore paintings and the renowned interactive self-portrait.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                                }
                            }}
                            onClick={() => navigate('/bio')}
                        >
                            <Box 
                                sx={{ 
                                    p: 3, 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    backgroundColor: '#2c3e50'
                                }}
                            >
                                <Person sx={{ fontSize: 60, color: 'white' }} />
                            </Box>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                                <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                                    Biography
                                </Typography>
                                <Typography sx={{ color: '#7f8c8d', lineHeight: 1.7 }}>
                                    From Birmingham suburbs to published author. 
                                    A journey of resilience, creativity, and unexpected turns.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default HomePage;